import { Upload } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import { Button, formatDateTime, formatNumber, type Column } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { KeysetTable } from '../KeysetTable.tsx';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { FINANCE_WRITE_ROLE, useRemittances, type RemittanceSummary } from './finance.ts';
import { FinanceFrame } from './FinanceFrame.tsx';
import type { FinanceSection } from './FinanceSections.tsx';
import { ImportRemittanceSheet } from './ImportRemittanceSheet.tsx';

export type RemittancesPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  onOpen: (remittanceId: string) => void;
  onSection: (section: FinanceSection) => void;
};

export const RemittancesPage = ({ api, shipping, onOpen, onSection }: RemittancesPageProps) => {
  const { t, locale } = usePageContext();
  const canImport = useAuth().hasRole(FINANCE_WRITE_ROLE);
  const remittances = useRemittances(api);
  const carriers = useCarriers(shipping);
  const [importing, setImporting] = useState(false);
  const carrierName = (code: string): string => carrierNameOf(carriers.data?.items, code);
  const count = (value: number) => formatNumber(value, locale);

  const columns: readonly Column<RemittanceSummary>[] = [
    {
      id: 'createdAt',
      header: t('remittances.importedAt'),
      primary: true,
      cell: (row) => formatDateTime(row.createdAt, locale),
    },
    { id: 'carrier', header: t('remittances.carrier'), cell: (row) => carrierName(row.carrierCode) },
    { id: 'source', header: t('remittances.source'), cell: (row) => t(`remittances.source_${row.source}`) },
    { id: 'lines', header: t('remittances.lines'), numeric: true, cell: (row) => count(row.lineCount) },
    {
      id: 'settled',
      header: t('remittances.settled'),
      numeric: true,
      cell: (row) => count(row.settledCount),
    },
    {
      id: 'disputed',
      header: t('remittances.disputed'),
      numeric: true,
      cell: (row) => count(row.disputedCount),
    },
    {
      id: 'attention',
      header: t('remittances.attention'),
      numeric: true,
      cell: (row) => count(row.attentionCount),
    },
  ];

  return (
    <FinanceFrame
      section="remittances"
      context={t('remittances.context')}
      action={
        canImport ? (
          <Button
            icon={<Upload size={20} aria-hidden="true" />}
            onClick={() => {
              setImporting(true);
            }}
          >
            {t('remittances.importTitle')}
          </Button>
        ) : undefined
      }
      onSection={onSection}
    >
      <KeysetTable
        pages={remittances}
        columns={columns}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          onOpen(row.id);
        }}
        emptyTitle={t('remittances.none')}
        emptyDescription={t('remittances.noneHelp')}
      />
      {importing ? (
        <ImportRemittanceSheet
          api={api}
          shipping={shipping}
          onClose={() => {
            setImporting(false);
          }}
          onImported={(detail) => {
            setImporting(false);
            onOpen(detail.remittance.id);
          }}
        />
      ) : null}
    </FinanceFrame>
  );
};
